import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { ArrowUpDown, ArrowUp, ArrowDown, Loader2 } from 'lucide-react';
import { UserActionsMenu } from './UserActionsMenu';

function SortIcon({ field, sortField, sortDir }) {
  if (sortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
  return sortDir === 'asc' ? <ArrowUp className="w-3 h-3 ml-1" /> : <ArrowDown className="w-3 h-3 ml-1" />;
}

function formatDate(dateStr) {
  if (!dateStr) return '\u2014';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getDaysRemaining(deletedAt) {
  if (!deletedAt) return null;
  const deleted = new Date(deletedAt);
  const expiry = new Date(deleted.getTime() + 30 * 24 * 60 * 60 * 1000);
  return Math.max(0, Math.ceil((expiry - Date.now()) / (24 * 60 * 60 * 1000)));
}

export function UserTable({
  users, loading, activeTab, hasActiveFilters,
  sortField, sortDir, toggleSort,
  currentUserId,
  onViewAutomata, onExport, onImpersonate, onMessage,
  onResetPassword, onChangeRole, onArchive, onDelete, onRestore, onPermanentDelete,
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <p className="text-center text-muted-foreground py-8">
        {hasActiveFilters ? 'No users match the current filters' : `No ${activeTab} users`}
      </p>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('name')}>
            <span className="flex items-center">Name<SortIcon field="name" sortField={sortField} sortDir={sortDir} /></span>
          </TableHead>
          <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('email')}>
            <span className="flex items-center">Email<SortIcon field="email" sortField={sortField} sortDir={sortDir} /></span>
          </TableHead>
          <TableHead>Auth</TableHead>
          <TableHead className="cursor-pointer select-none text-center" onClick={() => toggleSort('automata_count')}>
            <span className="flex items-center justify-center">Automata<SortIcon field="automata_count" sortField={sortField} sortDir={sortDir} /></span>
          </TableHead>
          <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('created_at')}>
            <span className="flex items-center">Created<SortIcon field="created_at" sortField={sortField} sortDir={sortDir} /></span>
          </TableHead>
          <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('last_login')}>
            <span className="flex items-center">Last Login<SortIcon field="last_login" sortField={sortField} sortDir={sortDir} /></span>
          </TableHead>
          {activeTab === 'deleted' && <TableHead>Days Left</TableHead>}
          <TableHead className="text-right w-12"></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {users.map((u) => (
          <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
            <TableCell className="font-medium">
              <div className="flex items-center gap-2">
                {u.picture ? (
                  <img src={u.picture} alt="" className="w-7 h-7 rounded-full" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center text-xs">
                    {u.name?.[0]?.toUpperCase() || '?'}
                  </div>
                )}
                <span>{u.name}</span>
                {u.role === 'admin' && (
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-primary/10 text-primary">Admin</span>
                )}
              </div>
            </TableCell>
            <TableCell className="text-sm">{u.email}</TableCell>
            <TableCell>
              <span className={`text-xs ${u.google_linked ? 'text-blue-500' : 'text-muted-foreground'}`}>
                {u.google_linked && u.has_password !== false ? 'Both' : u.google_linked ? 'Google' : 'Email'}
              </span>
            </TableCell>
            <TableCell className="text-center">{u.automata_count || 0}</TableCell>
            <TableCell className="text-sm text-muted-foreground">{formatDate(u.created_at)}</TableCell>
            <TableCell className="text-sm text-muted-foreground">{formatDate(u.last_login)}</TableCell>
            {activeTab === 'deleted' && (
              <TableCell>
                <span className="text-xs font-medium text-amber-500">
                  {getDaysRemaining(u.deleted_at)}d
                </span>
              </TableCell>
            )}
            <TableCell className="text-right">
              <UserActionsMenu
                targetUser={u}
                currentUserId={currentUserId}
                activeTab={activeTab}
                onViewAutomata={onViewAutomata}
                onExport={onExport}
                onImpersonate={onImpersonate}
                onMessage={onMessage}
                onResetPassword={onResetPassword}
                onChangeRole={onChangeRole}
                onArchive={onArchive}
                onDelete={onDelete}
                onRestore={onRestore}
                onPermanentDelete={onPermanentDelete}
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
